import { Observable } from "rxjs";
import NetworkService from "../network/networkService";
import { helperFunctions } from "../helper";
import { URLConfig } from "../network/urlStore";
import { IAttributeTypeResponse, IEntryMeansReportResponse, IHeaderFooterResponse, IReportTrial, ITrialReportingLocation, ITrialReportingLocationAllResponse, IOutlierReportResponse } from "../models/ITrialReporting.model";
import { AgTechQueryParam, OdQueryParam } from "../enums";


export const getTrialReportingHeadersFooters = (
  queryParams: Array<{
    key: AgTechQueryParam|OdQueryParam;
    value: string[];
  }>
): Observable<IHeaderFooterResponse> => {
  let url = URLConfig.getReportingHeaderFooterURL();
  for (const param of queryParams) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IHeaderFooterResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching trial report headers/footers", error);
      }
    );
  });
};

// Fetch next page by nextPageUri URL
export const getTrialReportingHeaderFooterNextPage = (
  nextPageURI: string
): Observable<IHeaderFooterResponse> => {
  const url = URLConfig.getReportingHeadersNextPageURl(nextPageURI);

  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res) => {
        if (res) {
          const response = res as unknown as IHeaderFooterResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: any) => {
        subscriber.error(error);
        console.log("Error while fetching trial report headers/footers next page", error);
      }
    );
  });
};

export const getAllTrialReportingHeadersFooters = (
  queryParams: Array<{ key: AgTechQueryParam|OdQueryParam; value: string[] }>
): Observable<IHeaderFooterResponse["values"]> => {
  let allValues: IHeaderFooterResponse["values"] = [];

  return new Observable((subscriber) => {
    const errorHandler = (error: any) => {
      subscriber.error(error);
    };

    const responseHandler = (response: IHeaderFooterResponse) => {
      allValues = [...allValues, ...(response.values || [])];

      if (response.nextPageUri) {
        getTrialReportingHeaderFooterNextPage(response.nextPageUri).subscribe({
          next: responseHandler,
          error: errorHandler,
        });
      } else {
        subscriber.next(allValues);
        subscriber.complete();
      }
    };

    getTrialReportingHeadersFooters(queryParams).subscribe({
      next: responseHandler,
      error: errorHandler,
    });
  });
};

//AttributeType 
export const getAttributeTypes = (
  queryParams: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<IAttributeTypeResponse> => {
  let url = URLConfig.getReportingAttributesURL();
  for (const param of queryParams) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IAttributeTypeResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching attribute types", error);
      }
    );
  });
};

export const getCommercialEntryMeansReport = (
  requestPayload: any
): Observable<IEntryMeansReportResponse> => {
  const url = URLConfig.getCommercialEntryMeansReportURL();
  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IEntryMeansReportResponse;

          // If you need to do any data preprocessing or sorting, do here
          // For example:
          // const sortedData = _.sortBy(response?.values ?? [], 'someField');
          // const processedResponse = { ...response, values: sortedData };

          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching entry means report", error);
      }
    );
  });
};

export const getReportImage = (
  id: number
): Observable<any> => {
  const url = URLConfig.getReportImage(id);
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          subscriber.next(res);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching report image", error);
      }
    );
  });
};

export const getProductManagerReport = (
  requestPayload: any
): Observable<IEntryMeansReportResponse> => {
  const url = URLConfig.getProductManagerReportURL();
  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IEntryMeansReportResponse;

          // If you need to do any data preprocessing or sorting, do here
          // For example:
          // const sortedData = _.sortBy(response?.values ?? [], 'someField');
          // const processedResponse = { ...response, values: sortedData };

          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching entry means report", error);
      }
    );
  });
};

export const getQCReport = (
  requestPayload: any
): Observable<IEntryMeansReportResponse> => {
  const url = URLConfig.getQcReportURL();
  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IEntryMeansReportResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching entry means report", error);
      }
    );
  });
};

 export const getOutlierReport = (
  requestPayload: any
): Observable<IOutlierReportResponse> => {
  const url = URLConfig.getOutlierReportURL();
  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IOutlierReportResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching outlier report", error);
      }
    );
  });
};

export const getReportingTrials = (
  queryParams: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<IReportTrial> => {
  let url = URLConfig.getReportingTrialsURL();
  for (const param of queryParams) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IReportTrial;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching attribute types", error);
      }
    );
  });
};

export const getTrialsNextPage = (
  nextPageURI: string
): Observable<IReportTrial> => {
  const url = URLConfig.getReportingTrialsNextPageURl(nextPageURI);
   return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res) => {
        if (res) {
          const response = res as unknown as IReportTrial;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: any) => {
        subscriber.error(error);
        console.log("Error while fetching trial report headers/footers next page", error);
      }
    );
  });
};


export const getTrialReportingLocation = (
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<ITrialReportingLocationAllResponse> => {
  let url = URLConfig.getTrialReportingLocationURL();
  for (const param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as ITrialReportingLocationAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching trialLocation data", error);
      }
    );
  });
};

export const getTrialReportingLocationNextPage = (
  nextPageURI: string
): Observable<ITrialReportingLocationAllResponse> => {
  const url = URLConfig.getTrialReportingLocationNextPage(nextPageURI);

  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (locationResponse) => {
        if (locationResponse) {
          const locationsResponse =
            locationResponse as unknown as ITrialReportingLocationAllResponse;

          subscriber.next(locationsResponse);
        }

        subscriber.complete();
      },
      (error: any) => {
        subscriber.error(error);
        console.log("Error while fetching Locations master data", error);
      }
    );
  });
};

export const getAllTrialReportingLocations = (
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<ITrialReportingLocation[]> => {
  let allTrialLocations: ITrialReportingLocation[] = [];
  return new Observable((subscriber) => {
    const errorHandler = (error: any) => {
      subscriber.error(error);
    };

    const responseHandler = (response: ITrialReportingLocationAllResponse) => {
      const trialLocationResponse = response;
      allTrialLocations = [
        ...allTrialLocations,
        ...(trialLocationResponse.trialLocations || []),
      ];

      if (trialLocationResponse.nextPageUri) {
        getTrialReportingLocationNextPage(trialLocationResponse.nextPageUri).subscribe({
          next: responseHandler,
          error: errorHandler,
          complete: () => {
            console.info("Completed fetching all trial locations");
          },
        });
      } else {
        subscriber.next(allTrialLocations);
        subscriber.complete();
      }
    };

    getTrialReportingLocation(queryParams).subscribe({
      next: responseHandler,
      error: errorHandler,
      complete: () => {
        console.info("Completed fetching all trial locations");
      },
    });
  });
};

export const getRepMeansReport = (
  requestPayload: any
): Observable<IEntryMeansReportResponse> => {
  const url = URLConfig.getRepMeansReportURL();
  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IEntryMeansReportResponse;

          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching rep means report", error);
      }
    );
  });
};